import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, SendIcon, ArrowLeftIcon, CheckCircleIcon } from 'lucide-react';
import { useContactSubmissions } from '../lib/cmsStore';
interface ContactModalProps {
  onClose: () => void;
  recipientEmail?: string;
}
const sectionVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay: 0.15 + i * 0.08,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function ContactModal({
  onClose,
  recipientEmail = 'your@email.com'
}: ContactModalProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const {
    addSubmission
  } = useContactSubmissions();
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  const handleSend = async () => {
    if (!form.message.trim() || !form.email.trim()) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch('https://formspree.io/f/mvzwqkdr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: form.name || 'Anonymous',
          email: form.email,
          message: form.message
        })
      });
      if (res.ok) {
        // Save to Supabase CMS inbox
        addSubmission({
          name: form.name || 'Anonymous',
          email: form.email,
          message: form.message,
          subject: 'Portfolio Contact'
        });
        setStatus('sent');
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data?.errors?.[0]?.message || 'Something went wrong. Try again.');
        setStatus('error');
      }
    } catch {
      setErrorMsg('Network error. Please try again.');
      setStatus('error');
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="fixed inset-0 z-50 bg-background overflow-y-auto" data-id="element-74">
      {/* Close button */}
      <button onClick={onClose} className="fixed top-6 right-6 md:top-8 md:right-12 z-50 p-2 text-muted-foreground hover:text-foreground transition-colors duration-200" aria-label="Close" data-id="element-75">
        <XIcon className="w-6 h-6" data-id="element-76" />
      </button>

      <div className="min-h-screen flex flex-col" data-id="element-77">
        <div className="flex-1 w-full max-w-[960px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-8" data-id="element-78">
          {/* Breadcrumb */}
          <motion.div custom={0} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center gap-2 mb-8 md:mb-10" data-id="element-79">
            <span className="font-mono text-xs text-primary tracking-[0.2em] uppercase" data-id="element-80">
              // Contact
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1 custom={1} variants={sectionVariants} initial="hidden" animate="visible" className="font-display text-[clamp(2.5rem,8vw,7rem)] font-bold leading-[0.9] tracking-tighter mb-6 text-foreground" data-id="element-81">
            Let's Talk
          </motion.h1>

          {/* Subtitle */}
          <motion.p custom={2} variants={sectionVariants} initial="hidden" animate="visible" className="font-mono text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl mb-16 md:mb-20" data-id="element-82">
            Hey, I'm always open to new conversations — whether it's a project
            idea, a collaboration, or just saying hi. Drop me a message and I'll
            get back to you.
          </motion.p>

          <div className="border-t border-border" data-id="element-83" />

          {status === 'sent' ? <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="py-20 md:py-32 flex flex-col items-center text-center" data-id="element-84">
              <CheckCircleIcon className="w-12 h-12 text-primary mb-6" data-id="element-85" />
              <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-3" data-id="element-86">
                Message Sent!
              </h2>
              <p className="font-mono text-sm text-muted-foreground max-w-md" data-id="element-87">
                Thanks for reaching out. I'll get back to you as soon as I can.
              </p>
              <button onClick={onClose} className="mt-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary hover:text-foreground transition-colors duration-300" data-id="element-88">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-89" />
                Back to Portfolio
              </button>
            </motion.div> : <>
              {/* Form */}
              <motion.div custom={3} variants={sectionVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14" data-id="element-90">
                <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-3" data-id="element-91">
                  Your Info
                </h3>
                <div className="space-y-4 max-w-lg" data-id="element-92">
                  <div data-id="element-93">
                    <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2 block" data-id="element-94">
                      Name
                    </label>
                    <input type="text" placeholder="What should I call you?" value={form.name} onChange={e => setForm(prev => ({
                  ...prev,
                  name: e.target.value
                }))} className="w-full px-0 py-3 bg-transparent border-b border-border font-mono text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary transition-colors" data-id="element-95" />
                  </div>
                  <div data-id="element-96">
                    <label className="font-mono text-xs text-muted-foreground uppercase tracking-widest mb-2 block" data-id="element-97">
                      Email *
                    </label>
                    <input type="email" placeholder="your@email.com" value={form.email} onChange={e => setForm(prev => ({
                  ...prev,
                  email: e.target.value
                }))} className="w-full px-0 py-3 bg-transparent border-b border-border font-mono text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary transition-colors" data-id="element-98" />
                  </div>
                </div>
              </motion.div>

              <div className="border-t border-border" data-id="element-99" />

              <motion.div custom={4} variants={sectionVariants} initial="hidden" animate="visible" className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-16 py-10 md:py-14" data-id="element-100">
                <h3 className="font-mono text-xs text-primary uppercase tracking-[0.2em] font-medium pt-3" data-id="element-101">
                  Message
                </h3>
                <div className="max-w-lg" data-id="element-102">
                  <textarea placeholder="What's on your mind? Tell me about your project, idea, or just say hi..." value={form.message} onChange={e => setForm(prev => ({
                ...prev,
                message: e.target.value
              }))} rows={6} className="w-full px-0 py-3 bg-transparent border-b border-border font-mono text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary transition-colors resize-none" data-id="element-103" />
                </div>
              </motion.div>

              {errorMsg && <div className="font-mono text-xs text-red-400 mb-4" data-id="element-104">
                  {errorMsg}
                </div>}

              <div className="border-t border-border" data-id="element-105" />

              {/* Footer */}
              <motion.div custom={5} variants={sectionVariants} initial="hidden" animate="visible" className="flex items-center justify-between py-8 md:py-10" data-id="element-106">
                <button onClick={onClose} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-300" data-id="element-107">
                  <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" data-id="element-108" />
                  Back
                </button>

                <button onClick={handleSend} disabled={!form.message.trim() || !form.email.trim() || status === 'sending'} className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-primary hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300" data-id="element-109">
                  {status === 'sending' ? 'Sending...' : <>
                      Send Message
                      <SendIcon className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" data-id="element-110" />
                    </>}
                </button>
              </motion.div>
            </>}
        </div>
      </div>
    </motion.div>;
}